import React from "react";
import { AlertTriangle } from "lucide-react";

const CancelAppointmentModal = ({ appointment, onClose, onConfirm, isAlreadyCancelled }) => {
  const formatDateTime = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  const REQUEST_TYPE_LABELS = {
    DROP_TRAILER: "Drop Trailer",
    LIVE_LOAD: "Live Load",
    };

  const isInTransit =
    typeof appointment?.shipmentStatus === "string" &&
    /\bin[\s_-]*transit\b/i.test(appointment.shipmentStatus);


  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black bg-opacity-30 px-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl p-6 space-y-6">
        {/* Header */}
        <div className="flex flex-col items-center space-y-3">
          <div className="bg-red-600 rounded-full p-3 text-white">
            <AlertTriangle size={32} />
          </div>
          <h2 className="text-2xl font-semibold text-gray-900">
            Cancel Appointment
          </h2>
        </div>

        {/* Warning Banner */}
        <div className="bg-red-50 border-l-4 border-red-600 px-4 py-3 rounded-md space-y-2">
          <div className="flex items-center text-gray-900 space-x-2">
            <AlertTriangle className="text-gray-900" size={18} />
            <div className="text-base">
              <span className="font-bold">Warning{" "}</span>
              <span className="ml-1 font-medium">
                {isAlreadyCancelled
                  ? "This appointment is already cancelled."
                  : isInTransit
                  ? "Appointments cannot be cancelled while the shipment is in transit"
                  : "Are you sure you want to cancel this appointment?"}
              </span>
            </div>
          </div>

          {!isAlreadyCancelled && (
            <p
              className={`text-sm pl-7 ${
                isInTransit ? "text-red-700" : "text-gray-900"
              }`}
            >
              {isInTransit
                ? "Once a shipment is in transit, the appointment is locked and cannot be cancelled."
                : "This action cannot be undone. The appointment will be permanently cancelled."}
            </p>
          )}
        </div>

        {/* Appointment Info Grid */}
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 grid grid-cols-1 sm:grid-cols-2 gap-6 text-sm text-gray-800">
          {/* Left Section */}
          <div>
            <h4 className="font-bold mb-2">Appointment Information</h4>
            <div className="space-y-1">
                <div>
                  <span className="font-medium">Shipment:</span>{" "}
                  {appointment?.shipmentNumber || "-"}
                  {appointment?.shipmentStatus
                    ? ` — ${appointment.shipmentStatus}`
                    : ""}
                </div>
              <div><span className="font-medium">Order #:</span> {appointment?.orderNumber || "-"}</div>
            <div>
            <span className="font-medium">Request Type:</span>{" "}
              {REQUEST_TYPE_LABELS[appointment?.requestType] || "-"}
            </div>
            </div>
          </div>

          {/* Right Section */}
          <div>
            <h4 className="font-bold mb-2">Schedule Details</h4>
            <div className="space-y-1">
              <div><span className="font-medium">Time:</span> {formatDateTime(appointment?.startTime)}</div>
              <div><span className="font-medium">Carrier:</span> {appointment?.carrierName || "-"}</div>
              <div><span className="font-medium">Dock:</span> {appointment?.dockName || "-"}</div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-100 text-sm transition"
          >
            Close
          </button>
          <button
            onClick={onConfirm}
            disabled={isAlreadyCancelled || isInTransit}
            className={`px-4 py-2 text-white text-sm rounded font-semibold transition
              ${isAlreadyCancelled || isInTransit ? "bg-gray-400 disabled:cursor-not-allowed" : "bg-red-600 hover:bg-red-700"}`}
          >
            Cancel Appointment
          </button>
        </div>
      </div>
    </div>
  );
};

export default CancelAppointmentModal;